import {
  type BaseQueryState,
  dehydrateQueryStateBase,
  dehydrateQueryStateToStringBase,
  hydrateQueryStateBase,
  hydrateQueryStateFromStringBase,
} from "@store/utils/queryStateUtils";
import type { Location } from "history";

import { DEFAULT_PAGE_LIMIT } from "@helpers/constants";

import type { ResourceItem } from "../../components/ResourceSelector/ResourceSelector";
import {
  defaultSortField,
  getDefaultSortOrder,
} from "../../modules/app/permissions/data/fields";
import type { Rule } from "../../modules/app/permissions/data/types";

export type RulesQueryState = BaseQueryState<Rule> & {
  /**
   * Resource picked in the search box, persisted in the URL (`resource` param)
   * so a shared/reloaded link restores the picker — not just the filtered table.
   */
  selectedResource?: ResourceItem | null;
  /**
   * View-as target user id, persisted in the URL (`viewAs` param) so a
   * reloaded/shared link restores whose permissions are being inspected —
   * the whole point of the feature. null/absent means the current user.
   * Not part of the RTK cache key string (see dehydrate…ToString): the query
   * already takes `userId` as a separate arg, so it keys on the user directly.
   */
  viewAsUserId?: string | null;
  /** Show only rules expiring within EXPIRING_SOON_DAYS; stored as `?expiring=true`. */
  expiringSoon: boolean;
  /** Show only already-expired rules (client-side filter over all policies); stored as `?expired=true`. */
  expired: boolean;
};

const QUERY_DEFAULTS = {
  defaultSort: {
    fieldName: defaultSortField.fieldName,
    direction: getDefaultSortOrder(defaultSortField),
  },
  defaultLimit: DEFAULT_PAGE_LIMIT,
} as const;

const SELECTED_RESOURCE_PARAM = "resource";
const VIEW_AS_PARAM = "viewAs";
const EXPIRING_PARAM = "expiring";
const EXPIRED_PARAM = "expired";

function parseSelectedResource(search: string): ResourceItem | null {
  const raw = new URLSearchParams(search.replace(/^\?/, "")).get(
    SELECTED_RESOURCE_PARAM,
  );
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ResourceItem;
  } catch {
    return null;
  }
}

export function hydrateRulesQueryState(location: Location): RulesQueryState {
  const params = new URLSearchParams(location.search.replace(/^\?/, ""));
  return {
    ...hydrateQueryStateBase<Rule>(location, QUERY_DEFAULTS),
    selectedResource: parseSelectedResource(location.search),
    viewAsUserId: params.get(VIEW_AS_PARAM),
    expiringSoon: params.get(EXPIRING_PARAM) === "true",
    expired: params.get(EXPIRED_PARAM) === "true",
  };
}

export function dehydrateRulesQueryState(opts: {
  queryState: RulesQueryState;
  location: Location;
}): URLSearchParams {
  const params = dehydrateQueryStateBase<Rule>(opts);
  if (opts.queryState.selectedResource) {
    params.set(
      SELECTED_RESOURCE_PARAM,
      JSON.stringify(opts.queryState.selectedResource),
    );
  } else {
    params.delete(SELECTED_RESOURCE_PARAM);
  }
  if (opts.queryState.viewAsUserId) {
    params.set(VIEW_AS_PARAM, opts.queryState.viewAsUserId);
  } else {
    params.delete(VIEW_AS_PARAM);
  }
  if (opts.queryState.expiringSoon) {
    params.set(EXPIRING_PARAM, "true");
  } else {
    params.delete(EXPIRING_PARAM);
  }
  if (opts.queryState.expired) {
    params.set(EXPIRED_PARAM, "true");
  } else {
    params.delete(EXPIRED_PARAM);
  }
  return params;
}

// The serialized form is the RTK Query cache key; it omits selectedResource
// (display-only) so picking a resource doesn't churn the key beyond the
// expression filter it already changes.
export function hydrateRulesQueryStateFromString(
  searchString: string,
): RulesQueryState {
  return {
    ...hydrateQueryStateFromStringBase<Rule>(searchString, QUERY_DEFAULTS),
    expiringSoon: false,
    expired: false,
  };
}

export function dehydrateRulesQueryStateToString(
  queryState: RulesQueryState,
): string {
  return dehydrateQueryStateToStringBase<Rule>(queryState);
}
